// 메시지방 캐시 (sessionStorage).
// 목적: 목록 재진입과 방 진입 시 헤더/프로필을 즉시 보여 체감 로딩을 줄인다.
// - API 계약과 무관한 FE 전용 캐시다. 항상 fetch 가 최종 진실이며 캐시는 first paint 용이다.
// - 사용자별 키를 사용해 같은 탭에서 계정을 바꿔도 이전 계정의 방/프로필이 노출되지 않게 한다.
// - sessionStorage 접근은 프라이빗 모드/WebView 에서 던질 수 있어 모두 try/catch 로 감싼다.

const CACHE_PREFIX = 'buccl.chat';
const EMPTY_PREVIEW = '아직 메시지가 없습니다.';
const normalizeUserId = (userId) => String(userId || '').trim();
const roomsKey = (userId) => `${CACHE_PREFIX}.${normalizeUserId(userId)}.rooms`;
const roomKey = (userId, roomId) => `${CACHE_PREFIX}.${normalizeUserId(userId)}.room.${roomId}`;

// 의미 있는 미리보기 문자열인지 판정한다.
// 빈 문자열/공백뿐/placeholder("아직 메시지가 없습니다.")는 "내용 없음"으로 본다.
// 단, 미래에 BE/기획이 "마지막 메시지를 명시적으로 삭제/없음 처리"하는 계약을 추가하면
// placeholder 보존/복원 정책은 별도 플래그로 구분해야 한다.
const isMeaningfulPreview = (value) => {
  const text = String(value ?? '').trim();
  return Boolean(text) && text !== EMPTY_PREVIEW;
};

// 방 요약 캐시 merge 정책.
// 방 상세(getMessageRoom) 응답에는 lastMessage/lastMessageAt 요약이 없어, 매핑 결과가
// 빈 lastMessageAt('') 과 placeholder lastMessage 를 만든다. 이 값으로 목록(list)에서 만든
// 정상 요약을 덮으면 캐시가 오염되므로(QA: buccl.chat.<user>.room.<id>), 표시용 요약 필드는
// 들어온 값이 비었거나 placeholder 면 기존의 의미 있는 값을 유지한다.
const mergeRoomSummary = (previous, next) => {
  if (!next || typeof next !== 'object') return previous || null;
  if (!previous || typeof previous !== 'object') return next;
  const merged = { ...previous, ...next };
  if (!String(next.lastMessageAt ?? '').trim() && String(previous.lastMessageAt ?? '').trim()) {
    merged.lastMessageAt = previous.lastMessageAt;
  }
  if (!isMeaningfulPreview(next.lastMessage) && isMeaningfulPreview(previous.lastMessage)) {
    merged.lastMessage = previous.lastMessage;
  }
  return merged;
};

const safeSession = () => {
    try {
        if (typeof window === 'undefined' || !window.sessionStorage) return null;
        return window.sessionStorage;
    } catch {
        return null;
    }
};

const readJson = (store, key) => {
    try {
        const raw = store.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed ?? null;
    } catch {
        return null;
    }
};

const writeJson = (store, key, value) => {
    try {
        store.setItem(key, JSON.stringify(value));
    } catch {
        // 용량 초과/직렬화 실패는 캐시 미스로 흡수한다. 캐시는 부가 기능이다.
    }
};

const removeItem = (store, key) => {
    try {
        store.removeItem(key);
    } catch {
        // 캐시 삭제 실패는 fetch 진실 경로를 막지 않는다.
    }
};

export const readCachedRooms = (userId) => {
    const scopedUserId = normalizeUserId(userId);
    if (!scopedUserId) return [];
    const store = safeSession();
    if (!store) return [];
    const parsed = readJson(store, roomsKey(scopedUserId));
    return Array.isArray(parsed) ? parsed : [];
};

export const writeCachedRooms = (userId, rooms = []) => {
    const scopedUserId = normalizeUserId(userId);
    const store = safeSession();
    if (!scopedUserId || !store || !Array.isArray(rooms)) return;
    // 방 본문(messages)은 캐시하지 않는다. 헤더/프로필/미리보기에 필요한 요약만 둔다.
    const summaries = rooms.map(({ messages, ...summary }) => summary);
    writeJson(store, roomsKey(scopedUserId), summaries);
};

// 방 진입 시 first paint 용 요약을 돌려준다.
// 개별 방 캐시 → 목록 캐시 순으로 찾고, 없으면 null.
export const readCachedRoom = (userId, roomId) => {
    const scopedUserId = normalizeUserId(userId);
    if (!scopedUserId || roomId == null || roomId === '') return null;
    const store = safeSession();
    const fromList = readCachedRooms(scopedUserId).find((room) => String(room.id) === String(roomId)) || null;
    if (store) {
        const direct = readJson(store, roomKey(scopedUserId, roomId));
        if (direct && typeof direct === 'object') {
            // 과거 버전이 저장한 오염된(빈 lastMessageAt / placeholder lastMessage) direct 캐시도
            // 목록 캐시의 정상 요약으로 보강해 헤더/미리보기가 깨지지 않게 read 시 merge 한다.
            return mergeRoomSummary(fromList, direct);
        }
    }
    return fromList;
};

export const writeCachedRoom = (userId, room) => {
    const scopedUserId = normalizeUserId(userId);
    const store = safeSession();
    if (!scopedUserId || !store || !room?.id) return;
    const { messages, ...summary } = room;
    // 기존 direct/list 요약과 merge 해, 방 상세의 빈/placeholder 요약이 정상 summary 를 덮지 않게 한다.
    const previous = readJson(store, roomKey(scopedUserId, room.id))
        || readCachedRooms(scopedUserId).find((cached) => String(cached.id) === String(room.id))
        || null;
    writeJson(store, roomKey(scopedUserId, room.id), mergeRoomSummary(previous, summary));
};

export const removeCachedRoom = (userId, roomId) => {
    const scopedUserId = normalizeUserId(userId);
    const store = safeSession();
    if (!scopedUserId || !store || roomId == null || roomId === '') return;
    removeItem(store, roomKey(scopedUserId, roomId));
    const nextRooms = readCachedRooms(scopedUserId).filter((room) => String(room.id) !== String(roomId));
    writeCachedRooms(scopedUserId, nextRooms);
};
